import { useState, type ReactNode } from 'react';
import Chip from '@mui/material/Chip';
import Person2OutlinedIcon from '@mui/icons-material/Person2Outlined';
import ReceiptLongOutlinedIcon from '@mui/icons-material/ReceiptLongOutlined';
import HandymanOutlinedIcon from '@mui/icons-material/HandymanOutlined';
import GppMaybeOutlinedIcon from '@mui/icons-material/GppMaybeOutlined';
import SecurityOutlinedIcon from '@mui/icons-material/SecurityOutlined';
import VerifiedUserOutlinedIcon from '@mui/icons-material/VerifiedUserOutlined';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import WifiOffOutlinedIcon from '@mui/icons-material/WifiOffOutlined';
import ExpandMoreOutlinedIcon from '@mui/icons-material/ExpandMoreOutlined';
import type { SvgIconComponent } from '@mui/icons-material';
import { Link } from 'react-router-dom';
import { PageHero } from '@/components/PageHero';
import { Consolidation } from '@/components/Consolidation';
import { NotifyForm } from '@/components/NotifyForm';
import { Reveal } from '@/components/Reveal';
import { useSeo } from '@/seo/useSeo';

type Reality = { icon: SvgIconComponent; title: string; body: string };

const realities: Reality[] = [
  {
    icon: Person2OutlinedIcon,
    title: 'Every hat is your hat',
    body: 'At a fifty-person MSP, ticketing, billing, security, and sales are different people’s jobs. At five employees or fewer, they’re the same two or three people wearing all of them, all week.',
  },
  {
    icon: ReceiptLongOutlinedIcon,
    title: 'Vendor sprawl costs more, relatively',
    body: 'Six subscriptions and six invoices are a rounding error at fifty seats. At your size, it’s a real slice of monthly overhead — and a real slice of someone’s Monday reconciling them.',
  },
  {
    icon: HandymanOutlinedIcon,
    title: 'No one to own the glue',
    body: 'An integration between your PSA and RMM breaking quietly costs an afternoon at any size. At your size, that afternoon is probably yours — probably during a week you don’t have it to spare.',
  },
  {
    icon: GppMaybeOutlinedIcon,
    title: 'Clients expect the same answers regardless of your size',
    body: 'A five-person MSP’s clients ask the same vendor security questionnaire a two-hundred-person MSP’s clients do. Tenant isolation and a real compliance posture can’t be a “someday” answer just because the team is small.',
  },
];

type Inherited = { icon: SvgIconComponent; title: string; body: string; linkTo: string; linkLabel: string };

const inherited: Inherited[] = [
  {
    icon: SecurityOutlinedIcon,
    title: 'Isolation enforced at the database',
    body: 'PostgreSQL row-level security keyed per tenant — the same guarantee whether you’re running one client or one hundred.',
    linkTo: '/blog/tenant-isolation-is-a-database-guarantee',
    linkLabel: 'How it works',
  },
  {
    icon: VerifiedUserOutlinedIcon,
    title: 'Every remote job is cryptographically signed',
    body: 'Ed25519-signed jobs against a default-deny capability registry — the same authorization model on a two-person team’s account as anyone else’s.',
    linkTo: '/blog/signed-jobs-cryptographic-endpoint-authorization',
    linkLabel: 'The signed-job architecture',
  },
  {
    icon: LockOutlinedIcon,
    title: 'Secrets encrypted, fail-closed',
    body: 'Integration credentials are encrypted at rest by default — not an add-on you have to know to ask for once you’re bigger.',
    linkTo: '/platform',
    linkLabel: 'See the platform',
  },
  {
    icon: WifiOffOutlinedIcon,
    title: 'Zero inbound site-firewall exposure',
    body: 'The monitoring agent never listens for anything, at any client site — no port to open, no rule to justify at your next security review.',
    linkTo: '/blog/why-our-monitoring-agent-is-outbound-only',
    linkLabel: 'Why it’s outbound-only',
  },
];

type Faq = { q: string; a: ReactNode };

const faqs: Faq[] = [
  {
    q: 'We’re a team of two or three — is joining private beta worth the switching effort right now?',
    a: 'That’s a fair question to ask before committing any time. The design-partner cohort starts with a direct conversation about your specific stack and workload, not a sales script — we’ll tell you plainly whether Nexus is ready for what you need today, and if something you depend on isn’t there yet, we’d rather say so than oversell it.',
  },
  {
    q: 'What happens if a capability we need is still “building” or “on the roadmap”?',
    a: (
      <>
        Every module carries an honest status — live in our own practice, available on configure, actively
        building, or on the roadmap — and that status is public on the{' '}
        <Link to="/platform" className="text-nexus-purple-light no-underline hover:underline">
          platform pages
        </Link>
        , not something you discover after signing up. Design partners get direct input into what gets built
        next, because private beta is meant to shape the build, not just consume it.
      </>
    ),
  },
  {
    q: 'Can we get our own data out if it doesn’t work out?',
    a: 'Data portability is a question every vendor should answer specifically instead of vaguely — ask us for the current export path for your ticket history, device inventory, and client data, and we’ll show you exactly what exists today rather than a roadmap promise.',
  },
  {
    q: 'Will a platform built for a 1–5 person team still work once we grow past that?',
    a: 'Yes — the architecture underneath (tenant isolation, role-based access, signed jobs) doesn’t change as you add headcount; you’re not on a “starter” version of anything that gets swapped out later. What’s still genuinely maturing is the AI layer’s autonomy and a few newer modules — the roadmap page shows exactly where each one stands.',
  },
  {
    q: 'We don’t have a dedicated security or compliance person. Can we realistically keep up with something like SOC 2 or NIST CSF?',
    a: (
      <>
        That’s precisely the gap compliance posture tracking is built to close for a small team — a live
        dashboard and an automated QBR instead of a fire drill assembled by a person you don’t have. See the{' '}
        <Link to="/platform/compliance-qbr" className="text-nexus-purple-light no-underline hover:underline">
          compliance &amp; QBR module
        </Link>{' '}
        for the detail.
      </>
    ),
  },
];

function FaqItem({ item, index }: { item: Faq; index: number }): JSX.Element {
  const [open, setOpen] = useState(index === 0);
  return (
    <div className="overflow-hidden rounded-2xl border border-white/10 bg-white/[0.02]">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        className="flex w-full items-center justify-between gap-4 px-5 py-4 text-left"
      >
        <span className="font-display text-[15px] font-semibold text-nexus-ink">{item.q}</span>
        <ExpandMoreOutlinedIcon
          fontSize="small"
          className={`flex-shrink-0 text-white/50 transition-transform duration-200 ${open ? 'rotate-180' : ''}`}
        />
      </button>
      {open && <p className="px-5 pb-4 text-sm leading-relaxed text-white/70">{item.a}</p>}
    </div>
  );
}

export function ForSmallMsps(): JSX.Element {
  useSeo({
    title: 'Built for the 1–5 person MSP',
    description:
      'Nexus consolidates the PSA, RMM, backup, security, CRM, and compliance tools a small MSP stitches together into one platform — because a team of one to five doesn’t have a spare person to manage six vendor logins.',
    path: '/for-small-msps',
  });

  return (
    <div>
      <PageHero
        eyebrow="For 1–5 person MSPs"
        title={
          <>
            You&apos;re already the tech, the biller, and the security officer.
            <br className="hidden md:block" /> Your platform shouldn&apos;t add a seventh hat.
          </>
        }
        subtitle="Nexus consolidates the tools a small MSP stitches together — PSA, RMM, backup, security, compliance, CRM — into one platform, because a team of one to five doesn’t have a spare person to manage six vendor logins."
      >
        <Reveal delay={150}>
          <div className="mt-9 flex justify-center">
            <NotifyForm />
          </div>
        </Reveal>
      </PageHero>

      <section className="py-16 md:py-24">
        <div className="container-content">
          <Reveal>
            <div className="mx-auto mb-14 max-w-2xl text-center">
              <span className="text-xs font-bold uppercase tracking-[0.14em] text-nexus-purple-light">
                The reality of running lean
              </span>
              <h2 className="mt-3 font-display text-3xl font-bold text-nexus-ink md:text-[36px]">
                What changes when there&apos;s no one else to hand it to.
              </h2>
              <p className="mt-4 text-white/70">
                Most RMM and PSA tools are built and priced for a mid-market MSP with a dedicated ops
                person. Most MSPs aren&apos;t that. Here&apos;s where the gap actually shows up.
              </p>
            </div>
          </Reveal>

          <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
            {realities.map((r, i) => {
              const Icon = r.icon;
              return (
                <Reveal key={r.title} delay={i * 90}>
                  <div className="h-full rounded-2xl border border-white/10 bg-white/[0.02] p-6">
                    <div className="mb-4 flex h-11 w-11 items-center justify-center rounded-xl bg-nexus-purple/[0.14] text-nexus-purple-light">
                      <Icon fontSize="small" />
                    </div>
                    <h3 className="font-display text-[17px] font-semibold text-nexus-ink">{r.title}</h3>
                    <p className="mt-1.5 text-sm text-white/65">{r.body}</p>
                  </div>
                </Reveal>
              );
            })}
          </div>
        </div>
      </section>

      <Consolidation />

      <section className="border-y border-white/10 bg-white/[0.015] py-16 md:py-24">
        <div className="container-content">
          <Reveal>
            <div className="mx-auto mb-14 max-w-2xl text-center">
              <span className="text-xs font-bold uppercase tracking-[0.14em] text-nexus-purple-light">
                Inherited, not upsold
              </span>
              <h2 className="mt-3 font-display text-3xl font-bold text-nexus-ink md:text-[36px]">
                The same architecture whether you have five clients or five hundred.
              </h2>
              <p className="mt-4 text-white/70">
                Tenant isolation, signed endpoint execution, and encrypted secrets aren&apos;t an
                &quot;enterprise tier&quot; at Nexus. Every tenant runs on the same guarantees from day
                one — including a design-partner cohort of one.
              </p>
            </div>
          </Reveal>

          <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
            {inherited.map((item, i) => {
              const Icon = item.icon;
              return (
                <Reveal key={item.title} delay={i * 90}>
                  <Link
                    to={item.linkTo}
                    className="group flex h-full flex-col rounded-2xl border border-white/10 bg-white/[0.02] p-6 no-underline transition-all duration-200 hover:-translate-y-1 hover:border-nexus-purple/50"
                  >
                    <div className="mb-4 flex h-11 w-11 items-center justify-center rounded-xl bg-nexus-purple/[0.14] text-nexus-purple-light transition-transform duration-200 group-hover:scale-110">
                      <Icon fontSize="small" />
                    </div>
                    <h3 className="font-display text-[17px] font-semibold text-nexus-ink">{item.title}</h3>
                    <p className="mt-1.5 flex-1 text-sm text-white/65">{item.body}</p>
                    <span className="mt-3 text-xs font-semibold text-nexus-purple-light">
                      {item.linkLabel} &rarr;
                    </span>
                  </Link>
                </Reveal>
              );
            })}
          </div>
        </div>
      </section>

      <section className="py-16 md:py-24">
        <div className="container-content">
          <Reveal>
            <div className="mx-auto mb-10 max-w-2xl text-center">
              <span className="text-xs font-bold uppercase tracking-[0.14em] text-nexus-purple-light">
                Straight answers
              </span>
              <h2 className="mt-3 font-display text-3xl font-bold text-nexus-ink md:text-[36px]">
                Questions small MSPs actually ask us.
              </h2>
            </div>
          </Reveal>
          <div className="mx-auto flex max-w-[720px] flex-col gap-3">
            {faqs.map((item, i) => (
              <Reveal key={item.q} delay={i * 60}>
                <FaqItem item={item} index={i} />
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="border-t border-white/10 py-16 md:py-24">
        <div className="container-content">
          <Reveal>
            <div className="relative overflow-hidden rounded-[28px] border border-nexus-purple/35 bg-gradient-to-br from-[#2a1450] to-[#120a26] p-8 text-center md:p-16">
              <div
                aria-hidden
                className="animate-drift pointer-events-none absolute -right-20 -top-20 h-[280px] w-[280px] rounded-full bg-nexus-purple/[0.25] blur-3xl"
              />
              <Chip
                label="Design-partner cohort — 1–5 person teams welcome"
                size="small"
                className="relative mb-4 border border-nexus-purple/35 bg-nexus-purple/[0.14] font-semibold text-nexus-purple-light"
              />
              <h2 className="relative font-display text-3xl font-bold text-nexus-ink md:text-[36px]">
                Stop managing your vendors instead of your clients.
              </h2>
              <p className="relative mx-auto mt-3 max-w-[520px] text-white/70">
                Nexus is live in our own MSP operations and opening to a limited design-partner
                cohort — teams of one to five are exactly who we built the consolidation for.
              </p>
              <div className="relative mt-8 flex justify-center">
                <NotifyForm />
              </div>
              <p className="relative mt-4 text-xs text-white/40">
                No spam, no lists sold, and no pressure to migrate everything on day one.
              </p>
            </div>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
